import { css } from '@emotion/react'
import { Cell, Flow, Grid, Text, Theme, useTheme } from 'bold-ui'
import { CheckboxField, TextAreaField, usePecField } from 'components/form'
import { SelectField } from 'components/form/core'
import { CidSelectField } from 'components/form/field/select/CidSelectField/CidSelectField'
import { MetaRoot } from 'util/metaPath'

import { ClassificacaoRiscoField } from '../components/ClassificacaoRiscoField'
import {
  CentralExecutanteSelectModel,
  ProcedimentoRegulacaoSelectModel,
  SolicitacaoRegulacaoModel,
  TipoSolicitacaoRegulacaoEnum,
} from '../model-esusRegulacao'

interface EsusRegulacaoFormFieldsProps {
  path: MetaRoot<SolicitacaoRegulacaoModel>
  tipo: TipoSolicitacaoRegulacaoEnum
}

export function EsusRegulacaoFormFields(props: Readonly<EsusRegulacaoFormFieldsProps>) {
  const { path, tipo } = props
  const theme = useTheme()
  const styles = createStyles(theme)

  const {
    input: { value: possuiAlergia },
  } = usePecField({ name: path.possuiAlergia.absolutePath(), subscription: { value: true } })

  const {
    input: { value: possuiDeficiencia },
  } = usePecField({ name: path.possuiDeficiencia.absolutePath(), subscription: { value: true } })

  const {
    input: { value: solicitacaoJudicializada },
  } = usePecField({ name: path.solicitacaoJudicializada.absolutePath(), subscription: { value: true } })

  return (
    <Grid>
      <Cell size={12}>
        <div css={styles.container}>
          <Text style={styles.title}>Dados da solicitação</Text>

          <Grid>
            <Cell size={12}>
              <SelectField<ProcedimentoRegulacaoSelectModel>
                name={path.procedimento}
                label='Procedimento'
                required
                style={{ width: '100%' }}
                items={[]}
                itemToString={(item) => item?.nome}
              />
            </Cell>

            {tipo === TipoSolicitacaoRegulacaoEnum.CONSULTA && (
              <Cell size={12}>
                <CheckboxField name={path.possuiConsultaRetorno} label='Consulta de retorno' />
              </Cell>
            )}

            <Cell size={12}>
              <SelectField<CentralExecutanteSelectModel>
                name={path.centralExecutante}
                label='Central executante'
                required
                style={{ width: '100%' }}
                items={[]}
                itemToString={(item) => item?.nome}
              />
            </Cell>
          </Grid>
        </div>
      </Cell>

      <Cell size={12}>
        <div css={styles.container}>
          <Text style={styles.title}>Problemas/condições</Text>

          <Flow direction='vertical' gap={0.75}>
            <Grid>
              <Cell size={12}>
                <CidSelectField name={path.cid10} label='CID-10' required />
              </Cell>
            </Grid>

            <Flow direction='vertical' gap={0.5}>
              <Text fontWeight='bold'>Outros</Text>

              <Grid>
                <Cell size={6}>
                  <CheckboxField name={path.possuiAlergia} label='Alergia' />
                  {possuiAlergia && (
                    <div css={styles.descricaoWrapper}>
                      <TextAreaField
                        name={path.descricaoAlergia}
                        maxLength={200}
                        rows={1}
                        placeholder='Informe o quadro alérgico'
                        resize='none'
                      />
                    </div>
                  )}
                </Cell>
                <Cell size={6}>
                  <CheckboxField name={path.possuiDeficiencia} label='Pessoa com Deficiência (PcD)' />
                  {possuiDeficiencia && (
                    <div css={styles.descricaoWrapper}>
                      <TextAreaField
                        name={path.descricaoDeficiencia}
                        maxLength={200}
                        rows={1}
                        placeholder='Descreva a deficiência'
                        resize='none'
                      />
                    </div>
                  )}
                </Cell>
              </Grid>
            </Flow>
          </Flow>
        </div>
      </Cell>

      <Cell size={12} alignSelf='flex-start'>
        <ClassificacaoRiscoField name={path.classificacaoRisco} required />
      </Cell>

      <Cell size={12}>
        <TextAreaField
          name={path.justificativa}
          label='Justificativa do procedimento'
          required
          maxLength={2000}
          rows={3}
          resize='vertical'
        />
      </Cell>

      <Cell size={6}>
        <CheckboxField name={path.solicitacaoJudicializada} label='Solicitação judicializada' />
        {solicitacaoJudicializada && (
          <div css={styles.descricaoWrapper}>
            <TextAreaField
              name={path.descricaoSolicitacaoJudicializada}
              maxLength={200}
              rows={1}
              placeholder='Dados judiciais'
              resize='none'
            />
          </div>
        )}
      </Cell>
    </Grid>
  )
}

const createStyles = (theme: Theme) => ({
  container: css`
    position: relative;
    margin-top: 0.5rem;
    padding: 1rem;
    border: 1px solid ${theme.pallete.gray.c80};
  `,
  title: css`
    font-weight: bold;
    position: absolute;
    top: -0.75rem;
    margin-left: -0.75rem;
    padding: 0 0.5rem;
    background-color: ${theme.pallete.surface.main};
  `,
  descricaoWrapper: css`
    margin-top: 0.5rem;
  `,
})
